import { useState, useEffect } from "react";
import PublicIcon from "./brand/PublicIcon";
import "../styles/nexus-public.css";
import { getConsentStatus, saveConsent } from "../utils/cookieConsent";
import {
  initializeHostedAnalytics,
  shutdownHostedAnalytics,
} from "../utils/hostedAnalytics";
import { MS, usePresence } from "./ui/motion";

/**
 * CookieConsent
 *
 * One line on elevation 3, bottom left on every page (inside the workspace
 * it clears the sidebar: components/layout/workspace.css), with explicit
 * accept and decline actions. Dismissing declines. It rises in, and on a
 * choice it sinks out before unmounting (usePresence). The choice is stored
 * as a domain-level cookie.
 */
const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const presence = usePresence(isVisible, { exitMs: MS.exitSlow });

  useEffect(() => {
    setIsClient(true);

    // Check if user has already made a choice (domain-level cookie)
    const userConsent = getConsentStatus();
    // Show banner only if user hasn't made a choice yet (first visit)
    if (userConsent === null) {
      // Let the first viewport settle before the banner enters.
      const timer = setTimeout(() => setIsVisible(true), 2500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    saveConsent(true);
    initializeHostedAnalytics();
    setIsVisible(false);
  };

  const handleDecline = () => {
    saveConsent(false);
    shutdownHostedAnalytics();
    setIsVisible(false);
  };

  const handleDismiss = () => {
    saveConsent(false);
    shutdownHostedAnalytics();
    setIsVisible(false);
  };

  // Don't render on server or if not client-ready
  if (!isClient || !presence.mounted) {
    return null;
  }

  return (
    <div
      className="nexus-public nx-cookie"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent banner"
      data-state={presence.state}
      onAnimationEnd={presence.onAnimationEnd}
    >
      <p>
        <span className="nx-cookie-mobile">
          Optional analytics help improve NexusDialer.{" "}
        </span>
        <span className="nx-cookie-desktop">
          Optional analytics cookies help us improve NexusDialer.{" "}
        </span>
        {/* This deployment's own policy page, not the marketing site. */}
        <a href="/privacy" className="nx-public-link">
          Privacy<span className="nx-cookie-desktop"> policy</span>
        </a>
      </p>
      <div className="nx-cookie-actions">
        <button
          type="button"
          onClick={handleDecline}
          className="nx-public-button"
          aria-label="Decline optional analytics"
        >
          Decline
        </button>
        <button
          type="button"
          onClick={handleAccept}
          className="nx-public-button"
          aria-label="Accept optional analytics"
        >
          Accept
        </button>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        className="nx-cookie-close"
        aria-label="Dismiss and decline optional analytics"
      >
        <PublicIcon name="close" />
      </button>
    </div>
  );
};

export default CookieConsent;
